import type { Engine } from "../../engine/Engine";
import {
  DESIGNER_CLEAR_SELECTION_COMMAND,
  DESIGNER_DRAWER_STATE_EVENT,
  DESIGNER_DRAG_ENDED_EVENT,
  DESIGNER_DRAG_STARTED_EVENT,
  DESIGNER_ENABLE_DRAW_COMMAND,
  DESIGNER_GET_RENDER_MODE_COMMAND,
  DESIGNER_PRODUCT_DELETED_EVENT,
  DESIGNER_PRODUCT_TRANSFORMED_EVENT,
  DESIGNER_RENDER_MODE_CHANGED_EVENT,
  DESIGNER_REQUEST_RENDER_COMMAND,
  DESIGNER_SCENE_CHANGED_EVENT,
  DESIGNER_SCENE_LOADED_EVENT,
  DESIGNER_SCENE_READY_EVENT,
  DESIGNER_SELECTABLE_OFF_COMMAND,
  DESIGNER_SELECTABLE_ON_COMMAND,
  DESIGNER_SELECT_PRODUCT_COMMAND,
  DESIGNER_SELECT_WALL_COMMAND,
  DESIGNER_SELECTION_CHANGED_EVENT,
  type DesignerConfigurationCommandMap,
  type DesignerConfigurationEventMap,
  type DesignerInputEventMap,
  type DesignerSelectionCommandMap,
  type DesignerSelectionEventMap,
  type DesignerTransformCommandMap,
  type DesignerTransformEventMap,
} from "./DesignerEventContracts";
import { TypedEventChannel } from "./TypedEventChannel";

export class DesignerElementEventChannels {
  readonly input = {
    events: new TypedEventChannel<DesignerInputEventMap>(),
  };

  readonly selection = {
    commands: new TypedEventChannel<DesignerSelectionCommandMap>(),
    events: new TypedEventChannel<DesignerSelectionEventMap>(),
  };

  readonly transform = {
    commands: new TypedEventChannel<DesignerTransformCommandMap>(),
    events: new TypedEventChannel<DesignerTransformEventMap>(),
  };

  readonly configuration = {
    commands: new TypedEventChannel<DesignerConfigurationCommandMap>(),
    events: new TypedEventChannel<DesignerConfigurationEventMap>(),
  };
}

/**
 * One instance belongs to one sugar-canvas. The legacy Engine bridge is
 * intentionally bidirectional so old public listeners and registerEvents users
 * continue to work while element internals use viewport-local typed channels.
 */
export class ElementEventPlatform {
  readonly designer = new DesignerElementEventChannels();

  private _engine?: Engine;
  private _detachLegacyBridge: Array<() => void> = [];
  private _bridging = false;

  bindLegacyEngine(engine: Engine): void {
    if (this._engine === engine) return;
    this.unbindLegacyEngine();
    this._engine = engine;

    this._bridge(this.designer.selection.commands, [
      DESIGNER_SELECT_PRODUCT_COMMAND,
      DESIGNER_SELECT_WALL_COMMAND,
      DESIGNER_CLEAR_SELECTION_COMMAND,
      DESIGNER_SELECTABLE_ON_COMMAND,
      DESIGNER_SELECTABLE_OFF_COMMAND,
    ]);
    this._bridge(this.designer.selection.events, [
      DESIGNER_SELECTION_CHANGED_EVENT,
    ]);
    this._bridge(this.designer.transform.commands, [
      DESIGNER_REQUEST_RENDER_COMMAND,
    ]);
    this._bridge(this.designer.transform.events, [
      DESIGNER_DRAG_STARTED_EVENT,
      DESIGNER_DRAG_ENDED_EVENT,
      DESIGNER_PRODUCT_TRANSFORMED_EVENT,
      DESIGNER_PRODUCT_DELETED_EVENT,
    ]);
    this._bridge(this.designer.configuration.commands, [
      DESIGNER_GET_RENDER_MODE_COMMAND,
      DESIGNER_ENABLE_DRAW_COMMAND,
    ]);
    this._bridge(this.designer.configuration.events, [
      DESIGNER_SCENE_READY_EVENT,
      DESIGNER_SCENE_CHANGED_EVENT,
      DESIGNER_SCENE_LOADED_EVENT,
      DESIGNER_DRAWER_STATE_EVENT,
      DESIGNER_RENDER_MODE_CHANGED_EVENT,
    ]);
  }

  unbindLegacyEngine(): void {
    for (const detach of this._detachLegacyBridge.splice(0)) detach();
    this._engine = undefined;
  }

  private _bridge(channel: EventTarget, names: readonly string[]): void {
    const engine = this._engine;
    if (!engine) return;

    for (const name of names) {
      const toEngine = (event: Event): void => {
        if (this._bridging) return;
        this._bridging = true;
        try {
          engine.dispatchEvent(
            new CustomEvent(name, {
              detail: (event as CustomEvent<unknown>).detail,
            }),
          );
        } finally {
          this._bridging = false;
        }
      };
      const toChannel = (event: Event): void => {
        if (this._bridging) return;
        this._bridging = true;
        try {
          channel.dispatchEvent(
            new CustomEvent(name, {
              detail: (event as CustomEvent<unknown>).detail,
            }),
          );
        } finally {
          this._bridging = false;
        }
      };
      channel.addEventListener(name, toEngine);
      engine.addEventListener(name, toChannel);
      this._detachLegacyBridge.push(() => {
        channel.removeEventListener(name, toEngine);
        engine.removeEventListener(name, toChannel);
      });
    }
  }
}
